import {
  Box,
  Button,
  Dialog,
  DialogActions,
  Divider,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { Card, CardText } from "@ssf/components";
import { CheckValuesType } from "@ssf/memdb-api";
import { FileDocumentMultiple } from "mdi-material-ui";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import type { UseQueryClub } from "../lib/index.js";
import { DocumentDialogContent } from "./DocumentDialogContent.js";
import { type ClubDocument, useDocuments } from "./useDocuments.js";

const documentTypes = [
  CheckValuesType.Statutes,
  CheckValuesType.ConstituentMeeting,
  CheckValuesType.AnnualMeeting,
  CheckValuesType.AnnualReport,
  CheckValuesType.ClassList,
];

export const DocumentsCard = ({ club }: { club: UseQueryClub }) => {
  const { t } = useTranslation("DocumentsCard");
  const documents = useDocuments(club, documentTypes);

  if (!documents.length) {
    return null;
  }

  return (
    <Card title={t("title")} icon={<FileDocumentMultiple />} disablePadding>
      {t("description") && (
        <CardText sx={{ p: 2, mb: 0 }}>
          <Box sx={{ my: -2 }}>
            <Trans
              t={t}
              i18nKey="description"
              tOptions={{ postProcess: "markdown" }}
            />
          </Box>
        </CardText>
      )}
      <Documents documents={documents.filter(({ approved }) => approved)} />
    </Card>
  );
};

export const Documents = ({ documents }: { documents: ClubDocument[] }) => (
  <List disablePadding>
    {documents.map((document) => (
      <DocumentItem key={document.id} document={document} />
    ))}
  </List>
);

const DocumentItem = ({
  document,
}: {
  document: ReturnType<typeof useDocuments>[0];
}) => {
  const { t } = useTranslation("DocumentsCard");
  const [open, setOpen] = useState(false);

  return (
    <>
      <ListItemButton divider onClick={() => setOpen(true)}>
        <ListItemText>
          {t("document", {
            context: CheckValuesType[document.referenceid],
            ...document,
          })}
        </ListItemText>
      </ListItemButton>
      <Dialog open={open} onClose={() => setOpen(false)}>
        {open && (
          <>
            <DocumentDialogContent type="checkValue" data={document} />
            <Divider />
            <DialogActions>
              <Button
                variant="outlined"
                onClick={() => setOpen(false)}
                color="inherit"
              >
                {t("close")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
